import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, IconButton, InputAdornment } from '@mui/material';
import React from 'react';
import BaseInput, { BaseTextFieldProps } from './base-input.component';

type PasswordInputProps = {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    size?: 'medium' | 'small';
    /** 回车键触发登录/提交（登录页用） */
    onEnterLogin?: () => Promise<void>;
    placeholder?: string;
    /** 透传 BaseInput 内置校验；未传且 `noValidate` 为 false 时默认 `password`（6～20 位） */
    regularType?: BaseTextFieldProps['regularType'];
    /** 为 true 时关闭 BaseInput 正则（如 WiFi 密码由业务自行校验） */
    noValidate?: boolean;
    /** 为 true 时整行不可编辑 */
    disabled?: boolean;
    /**
     * 透传 `BaseInput` 的 `className`；未传时默认 `searchInput`（登录/弹窗沿用）。
     * 标签在表单项外部时可传 `''`，避免套用搜索框样式类。
     */
    className?: string;
};

/**
 * 密码输入框：右侧眼睛图标可切换显示/隐藏（与登录、Fiskaly 等复用）
 */
function PasswordInput(props: PasswordInputProps) {
    const {
        value,
        label,
        size,
        onChange,
        onEnterLogin,
        placeholder,
        regularType: regularTypeProp,
        noValidate = false,
        disabled = false,
        className: classNameProp,
    } = props;
    const regularType: BaseTextFieldProps['regularType'] | undefined = noValidate
        ? undefined
        : (regularTypeProp ?? 'password');
    const textFieldClassName = classNameProp === undefined ? 'searchInput' : classNameProp;
    const [showPassword, setShowPassword] = React.useState(false);

    /** 密码明文/密文切换 */
    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    return (
        <Box className="password">
            <BaseInput
                className={textFieldClassName}
                onChange={(event) => {
                    event.stopPropagation();
                    onChange(event.target.value);
                }}
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label={label}
                value={value}
                regularType={regularType}
                placeholder={placeholder}
                disabled={disabled}
                onKeyDown={async (e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') {
                        onEnterLogin && (await onEnterLogin?.());
                    }
                }}
                InputLabelProps={label ? { shrink: true } : undefined}
                InputProps={{
                    endAdornment: (
                        // 右侧留白：`edge="end"` 会把图标贴到描边内侧，与视觉稿间距不足
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                color="primary"
                                disabled={disabled}
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                size={size ?? 'medium'}
            />
        </Box>
    );
}

export default PasswordInput;